import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ChevronRight, Package, Plus, Radio } from "lucide-react";

import { Badge, lotStatusBadge } from "@/components/Badge";
import { EmptyState } from "@/components/EmptyState";
import { SectionHeader } from "@/components/SectionHeader";
import { StatCard } from "@/components/StatCard";
import {
  calcGlobalCostPerCard,
  calcLandedCost,
  calcNetProfit,
  useApp,
} from "@/context/AppContext";
import { apiFetch } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";

interface AuthMeResponse {
  id: string;
  ebayUsername: string;
}

export const Route = createFileRoute("/dashboard")({
  component: Dashboard,
});

function Dashboard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { lots, cards, products, sales, liveListings } = useApp();

  const me = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async (): Promise<AuthMeResponse | null> => {
      if (auth.isGuest) return null;
      const res = await apiFetch("/api/auth/me");
      if (!res.ok) throw new Error(`auth/me ${res.status}`);
      return (await res.json()) as AuthMeResponse;
    },
    enabled: auth.isAuthenticated,
  });

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const actionItems = useMemo(() => {
    const lotsToSort = lots.filter(
      (l) => l.status === "Unsorted" || l.status === "Sorting",
    ).length;
    const productsReady = products.filter(
      (p) => p.status === "Ready to List",
    ).length;
    const unshippedSales = sales.filter((s) => !s.shipped).length;
    const endingSoon = liveListings.filter((listing) => {
      if (!listing.endsAt) return false;
      const diff = new Date(listing.endsAt).getTime() - now;
      return diff > 0 && diff <= 86_400_000;
    }).length;
    const cardsWithPotential = cards.filter(
      (c) =>
        c.listingStatus === "Unlisted" &&
        c.quantity > 0 &&
        c.marketPrice > c.costBasis,
    ).length;
    return {
      lotsToSort,
      productsReady,
      unshippedSales,
      endingSoon,
      cardsWithPotential,
    };
  }, [lots, products, sales, liveListings, cards, now]);

  const hasActionItems =
    actionItems.lotsToSort > 0 ||
    actionItems.productsReady > 0 ||
    actionItems.unshippedSales > 0 ||
    actionItems.endingSoon > 0 ||
    actionItems.cardsWithPotential > 0;

  const stats = useMemo(() => {
    const totalCost = lots.reduce((s, l) => s + calcLandedCost(l), 0);
    const totalCards = lots.reduce((s, l) => s + l.estimatedCards, 0);
    const singlesListed = cards.filter(
      (c) => c.listingStatus === "Listed",
    ).length;
    const readyProducts = products.filter(
      (p) => p.status === "Ready to List",
    ).length;
    const totalSales = sales.reduce((s, sl) => s + sl.salePrice, 0);
    const netProfit = sales.reduce((s, sl) => s + calcNetProfit(sl), 0);

    const venueMap: Record<string, number> = {};
    for (const sl of sales) {
      venueMap[sl.venue] = (venueMap[sl.venue] ?? 0) + calcNetProfit(sl);
    }
    const topVenue = Object.entries(venueMap).sort((a, b) => b[1] - a[1])[0];

    const invCards = cards.filter((c) => c.listingStatus !== "Sold");
    const invCost = invCards.reduce((s, c) => s + c.costBasis * c.quantity, 0);
    const invValue = invCards.reduce(
      (s, c) =>
        s +
        ((c.expectedSalePrice > 0 ? c.expectedSalePrice : c.marketPrice) || 0) *
          c.quantity,
      0,
    );
    const invProfit = invValue - invCost;
    const projectedROI = invCost > 0 ? (invProfit / invCost) * 100 : 0;

    const liveCount = liveListings.length;
    const liveAskingValue = liveListings.reduce((s, l) => s + l.askingPrice, 0);

    const globalCostPerCard = calcGlobalCostPerCard(lots);

    return {
      totalCost,
      totalCards,
      inventoryCount: cards.length,
      singlesListed,
      readyProducts,
      totalSales,
      netProfit,
      topVenue,
      invValue,
      invProfit,
      projectedROI,
      liveCount,
      liveAskingValue,
      globalCostPerCard,
    };
  }, [lots, cards, products, sales, liveListings]);

  const recentLots = useMemo(
    () =>
      [...lots]
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, 3),
    [lots],
  );

  const recentLiveListings = useMemo(
    () =>
      [...liveListings]
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
        .slice(0, 3),
    [liveListings],
  );

  return (
    <section className="space-y-4">
      {/* Header */}
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[12px] font-medium tracking-wider text-muted-foreground">
            TCG Manager
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Dashboard
          </h1>
          {import.meta.env.DEV && auth.isGuest ? (
            <p className="text-xs text-muted-foreground">
              Browsing as guest (dev). Sign in with eBay to enable listing
              features.
            </p>
          ) : me.isLoading ? (
            <p className="text-xs text-muted-foreground">
              Loading your account…
            </p>
          ) : me.error ? (
            <p className="text-xs text-destructive">
              Could not load account: {String(me.error)}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Signed in as{" "}
              <span className="font-medium text-foreground">
                {me.data?.ebayUsername ?? "(unknown)"}
              </span>
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => void navigate({ to: "/lots/create" })}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          New Lot
        </button>
      </header>

      {/* Needs Attention */}
      {hasActionItems && (
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <h2 className="text-sm font-semibold text-foreground">
              Needs Attention
            </h2>
          </div>
          <div className="divide-y divide-border">
            {actionItems.lotsToSort > 0 && (
              <AttentionRow
                color="bg-amber-500"
                countColor="text-amber-500"
                label="Lots to sort"
                count={actionItems.lotsToSort}
                onClick={() => void navigate({ to: "/lots" })}
              />
            )}
            {actionItems.productsReady > 0 && (
              <AttentionRow
                color="bg-blue-500"
                countColor="text-blue-500"
                label="Products ready to list"
                count={actionItems.productsReady}
                onClick={() => void navigate({ to: "/products" })}
              />
            )}
            {actionItems.unshippedSales > 0 && (
              <AttentionRow
                color="bg-red-500"
                countColor="text-red-500"
                label="Unshipped sales"
                count={actionItems.unshippedSales}
                onClick={() => void navigate({ to: "/sales" })}
              />
            )}
            {actionItems.endingSoon > 0 && (
              <AttentionRow
                color="bg-orange-500"
                countColor="text-orange-500"
                label="Listings ending soon"
                count={actionItems.endingSoon}
                onClick={() => void navigate({ to: "/live" })}
              />
            )}
            {actionItems.cardsWithPotential > 0 && (
              <AttentionRow
                color="bg-green-500"
                countColor="text-green-500"
                label="Cards with listing potential"
                count={actionItems.cardsWithPotential}
                onClick={() => void navigate({ to: "/inventory" })}
              />
            )}
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label="Total Cost Basis"
          value={`$${stats.totalCost.toFixed(2)}`}
          sub="all lots"
        />
        <StatCard
          label="Net Profit"
          value={`$${stats.netProfit.toFixed(2)}`}
          positive={stats.netProfit > 0}
          negative={stats.netProfit < 0}
        />
        <StatCard
          label="Cards Acquired"
          value={stats.totalCards.toLocaleString()}
          sub={`${stats.inventoryCount.toLocaleString()} inventory`}
        />
        {stats.globalCostPerCard > 0 && (
          <StatCard
            label="Avg Cost/Card"
            value={`$${stats.globalCostPerCard.toFixed(4)}`}
            sub="portfolio avg"
          />
        )}
        <StatCard label="Singles Listed" value={String(stats.singlesListed)} />
        <StatCard
          label="Ready to List"
          value={String(stats.readyProducts)}
          sub="products"
          onClick={() => void navigate({ to: "/products" })}
        />
        <StatCard
          label="Live"
          value={String(stats.liveCount)}
          sub={
            stats.liveCount > 0
              ? `$${stats.liveAskingValue.toFixed(2)} asking`
              : "no active listings"
          }
          accent={stats.liveCount > 0}
          onClick={() => void navigate({ to: "/live" })}
        />
        <StatCard
          label="Inv. Value"
          value={`$${stats.invValue.toFixed(2)}`}
          sub="at expected price"
          accent
        />
        <StatCard
          label="Proj. ROI"
          value={`${stats.projectedROI >= 0 ? "+" : ""}${stats.projectedROI.toFixed(1)}%`}
          sub={`$${stats.invProfit >= 0 ? "+" : ""}${stats.invProfit.toFixed(2)} est. profit`}
          positive={stats.projectedROI > 0}
          negative={stats.projectedROI < 0}
        />
        <StatCard
          label="Total Sales"
          value={`$${stats.totalSales.toFixed(2)}`}
          accent
        />
      </div>

      {/* Top Venue */}
      {stats.topVenue && (
        <div className="rounded-md bg-primary p-4">
          <div className="text-[11px] font-medium tracking-wider text-primary-foreground/70">
            Top Performing Venue
          </div>
          <div className="text-xl font-semibold text-primary-foreground">
            {stats.topVenue[0]}
          </div>
          <div className="text-sm text-primary-foreground/80">
            ${stats.topVenue[1].toFixed(2)} net profit
          </div>
        </div>
      )}

      {/* Live Listings */}
      <div>
        <SectionHeader
          title="Live Listings"
          action="View All"
          onAction={() => void navigate({ to: "/live" })}
        />
        {recentLiveListings.length === 0 ? (
          <EmptyState
            icon={<Radio className="h-6 w-6" />}
            title="No active listings"
            description="Go live with a product to see it here"
            actionLabel="Go to Live"
            onAction={() => void navigate({ to: "/live" })}
          />
        ) : (
          <div className="space-y-2">
            {recentLiveListings.map((listing) => {
              const endsAtDiff = listing.endsAt
                ? new Date(listing.endsAt).getTime() - now
                : null;
              const isEndingSoon =
                endsAtDiff !== null &&
                endsAtDiff > 0 &&
                endsAtDiff <= 86_400_000;
              return (
                <button
                  key={listing.id}
                  type="button"
                  onClick={() => void navigate({ to: "/live" })}
                  className={`flex w-full items-center gap-3 rounded-md border bg-card p-3 text-left hover:bg-card/80 ${
                    isEndingSoon ? "border-destructive" : "border-border"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      isEndingSoon ? "bg-destructive" : "bg-green-500"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-semibold text-foreground">
                      {listing.title}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span>
                        {listing.venue} · {listing.cardCount}{" "}
                        {listing.cardCount === 1 ? "card" : "cards"}
                      </span>
                      {isEndingSoon && (
                        <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive">
                          <AlertCircle className="h-3 w-3" />
                          Ending Soon
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-primary">
                    ${listing.askingPrice.toFixed(2)}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Lots */}
      <div>
        <SectionHeader
          title="Recent Lots"
          action="View All"
          onAction={() => void navigate({ to: "/lots" })}
        />
        {recentLots.length === 0 ? (
          <EmptyState
            icon={<Package className="h-6 w-6" />}
            title="No lots yet"
            description="Intake your first purchase lot to get started"
            actionLabel="Add Lot"
            onAction={() => void navigate({ to: "/lots/create" })}
          />
        ) : (
          <div className="space-y-2">
            {recentLots.map((lot) => {
              const landed = calcLandedCost(lot);
              return (
                <button
                  key={lot.id}
                  type="button"
                  onClick={() =>
                    void navigate({
                      to: "/lots/$id",
                      params: { id: lot.id },
                    })
                  }
                  className="flex w-full items-center gap-3 rounded-md border border-border bg-card p-3 text-left hover:bg-card/80"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-medium tracking-wider text-muted-foreground">
                      {lot.id}
                    </div>
                    <div className="truncate text-sm font-semibold text-foreground">
                      {lot.seller || "Unknown Seller"}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {lot.purchaseDate}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="text-sm font-semibold text-foreground">
                      ${landed.toFixed(2)}
                    </div>
                    <Badge
                      label={lot.status}
                      variant={lotStatusBadge(lot.status)}
                    />
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

interface AttentionRowProps {
  color: string;
  countColor: string;
  label: string;
  count: number;
  onClick: () => void;
}
function AttentionRow({
  color,
  countColor,
  label,
  count,
  onClick,
}: AttentionRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 py-2 text-left hover:bg-muted/40"
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${color}`} />
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className={`text-sm font-semibold ${countColor}`}>{count}</span>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    </button>
  );
}
